<template>
    <section class="tk-engagements-panel">
        <div class="tk-engagements-panel__header">
            <h2>Enquiries</h2>
            <q-btn
                color="primary"
                no-caps
                unelevated
                icon="add"
                label="Create Enquiry"
                @click="openCreate"
            />
        </div>

        <div class="tk-engagements-filters row q-col-gutter-md">
            <div class="col-12 col-md-4">
                <q-input
                    v-model="filters.q"
                    outlined
                    dense
                    clearable
                    debounce="300"
                    label="Search Enquiries"
                    @update:model-value="refresh"
                >
                    <template v-slot:prepend><q-icon name="search" /></template>
                </q-input>
            </div>
            <div class="col-12 col-sm-6 col-md-2">
                <q-select
                    v-model="filters.method"
                    :options="methodOptions"
                    outlined
                    dense
                    clearable
                    emit-value
                    map-options
                    label="Method"
                    @update:model-value="refresh"
                />
            </div>
            <div class="col-12 col-sm-6 col-md-2">
                <q-select
                    v-model="filters.type"
                    :options="typeOptions"
                    outlined
                    dense
                    clearable
                    emit-value
                    map-options
                    label="Type"
                    @update:model-value="refresh"
                />
            </div>
            <div class="col-12 col-sm-6 col-md-2">
                <q-select
                    v-model="filters.staff"
                    :options="staffOptions"
                    outlined
                    dense
                    clearable
                    emit-value
                    map-options
                    label="Staff Member"
                    @update:model-value="refresh"
                />
            </div>
            <div class="col-12 col-sm-6 col-md-2">
                <q-select
                    v-model="filters.service"
                    :options="serviceOptions"
                    outlined
                    dense
                    clearable
                    emit-value
                    map-options
                    label="Service"
                    @update:model-value="refresh"
                />
            </div>
        </div>

        <q-table
            :rows="rows"
            :columns="columns"
            row-key="id"
            flat
            dense
            :loading="loading"
            :pagination="{ rowsPerPage: 10, sortBy: 'date', descending: true }"
            :rows-per-page-options="[10, 20, 50]"
            class="app-data-table tk-enquiries-table"
            @row-click="openEdit"
        >
            <template v-slot:body-cell-notes="props">
                <q-td :props="props" class="tk-enquiries-table__clamped-cell">
                    {{ notesSummary(props.row.notes) }}
                </q-td>
            </template>
            <template v-slot:no-data>
                <div class="full-width row flex-center text-grey-7 q-pa-md">
                    No enquiries found.
                </div>
            </template>
        </q-table>

        <TkEnquiryDrawer
            v-model="showDrawer"
            :enquiry="selectedEnquiry"
            :default-staff-id="currentStaffId"
            :method-options="methodOptions"
            :type-options="typeOptions"
            :staff-options="staffOptions"
            :service-options="serviceOptions"
            :saving="saving"
            @save="saveEnquiry"
        />
    </section>
</template>

<script>
import api from "@/services/api/api";
import notify from "@/services/util/notify";
import token from "@/services/util/roleHandler";
import TkEnquiryDrawer from "@/components/TkClients/TkEnquiryDrawer.vue";
import { formatDate, formatMinutes, staffOption } from "@/services/util/tkClients";

const errorMessage = (error, fallbackMessage) =>
    error.response?.data?.error || error.response?.statusText || fallbackMessage;

const lookupOption = (option) => ({ label: option.label, value: option.id });

export default {
    name: "TkEnquiries",
    components: { TkEnquiryDrawer },
    data() {
        return {
            loading: false,
            saving: false,
            showDrawer: false,
            selectedEnquiry: null,
            rows: [],
            services: [],
            staff: [],
            filters: { q: "", method: null, type: null, staff: null, service: null },
            methodOptions: [],
            typeOptions: [],
            columns: [
                { name: "date", label: "Date", field: "enquiry_date", format: value => formatDate(value), align: "left", sortable: true },
                { name: "enquirer", label: "Enquirer", field: "enquirer_name", align: "left", sortable: true },
                { name: "method", label: "Method", field: "method_label", align: "left", sortable: true },
                { name: "type", label: "Type", field: "type_label", align: "left", sortable: true },
                { name: "staff", label: "Staff Member", field: row => row.related_staff_name || row.related_staff_email || "-", align: "left", sortable: true },
                { name: "service", label: "Service", field: row => row.service_name || "-", align: "left", sortable: true },
                { name: "time", label: "Time Taken", field: row => row.time_spent_minutes == null ? "-" : formatMinutes(row.time_spent_minutes), align: "left" }
            ],
        };
    },
    computed: {
        staffOptions() {
            return this.staff.map(staffOption);
        },
        serviceOptions() {
            return this.services.map(service => ({ label: service.name, value: service.code }));
        },
        currentStaffId() {
            const email = String(token.getEmail() || "").toLowerCase();
            return this.staff.find(member => String(member.email || "").toLowerCase() === email)?.id || null;
        },
    },
    async mounted() {
        await Promise.all([this.loadOptions(), this.refresh()]);
    },
    methods: {
        notesSummary(value) {
            const text = String(value || "").trim();
            return text.length > 100 ? `${text.slice(0, 97)}...` : text || "-";
        },
        async loadOptions() {
            try {
                const [engagementLookupResponse, enquiryLookupResponse, staffResponse] = await Promise.all([
                    api.tkPeople.getEngagementLookups(),
                    api.lookupOptions.listOptions(["enquiry_method", "enquiry_type"]),
                    api.users.getAppStaffList(),
                ]);
                this.services = engagementLookupResponse.data.services || [];
                this.methodOptions = (enquiryLookupResponse.data?.enquiry_method || []).map(lookupOption);
                this.typeOptions = (enquiryLookupResponse.data?.enquiry_type || []).map(lookupOption);
                this.staff = staffResponse.data || [];
            } catch (error) {
                notify.error(errorMessage(error, "Failed to load enquiry options"), "top");
            }
        },
        async refresh() {
            this.loading = true;
            try {
                const response = await api.enquiries.listEnquiries({
                    q: this.filters.q || undefined,
                    method: this.filters.method || undefined,
                    type: this.filters.type || undefined,
                    staff: this.filters.staff || undefined,
                    service: this.filters.service || undefined,
                    limit: 200,
                });
                this.rows = response.data || [];
            } catch (error) {
                notify.error(errorMessage(error, "Failed to load enquiries"), "top");
            } finally {
                this.loading = false;
            }
        },
        openCreate() {
            this.selectedEnquiry = null;
            this.showDrawer = true;
        },
        openEdit(event, row) {
            if (event?.defaultPrevented) return;
            this.selectedEnquiry = row;
            this.showDrawer = true;
        },
        async saveEnquiry(enquiry) {
            this.saving = true;
            try {
                if (this.selectedEnquiry?.id) {
                    await api.enquiries.updateEnquiry(this.selectedEnquiry.id, enquiry);
                } else {
                    await api.enquiries.createEnquiry(enquiry);
                }
                this.showDrawer = false;
                await this.refresh();
                notify.primary("Enquiry saved", "top");
            } catch (error) {
                notify.error(errorMessage(error, "Failed to save enquiry"), "top");
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.tk-enquiries-table :deep(tbody tr) {
    cursor: pointer;
}

.tk-enquiries-table :deep(.tk-enquiries-table__clamped-cell) {
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
